import { DevLanguageSwitcher, displayLanguageSwitcher, ResetSitecoreStyles } from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { BaseLayout, FormLayout, ResultLayout } from '@hex/calculators-shared/layout';
import { trackCalculatorSubmit } from '@hex/shared/tracking';
import { defaultTheme, SapphireProvider, Flex, View, tokens } from '@sapphire-react/core';
import { useEffect, useRef, useState } from 'react';
import { Form } from './components/Form/Form';
import { Results } from './components/Results';
import { TipsContainer } from './components/Results/components/TipsContainer';
import { LoanCalculatorForm } from './types';

type Props = {
  locale?: string;
};

export function App({ locale }: Props) {
  const [formData, setFormData] = useState<LoanCalculatorForm>();

  const layoutRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const scrollForResult = formData && (isExtraSmall || isSmall);

  const scrollIntoCalculatorTop = () => {
    if (!isExtraSmall && !isSmall) {
      const sitecoreNavbarHeight = 102;
      const top = layoutRef?.current
        ? layoutRef?.current?.getBoundingClientRect().top + window.scrollY - sitecoreNavbarHeight
        : undefined;

      window?.scrollTo({ top });
    }
  };

  async function onSubmit(data: LoanCalculatorForm) {
    setFormData(data);
    trackCalculatorSubmit(data.loanAmount.toString());
    scrollIntoCalculatorTop();
  }

  useEffect(() => {
    if (scrollForResult) {
      layoutRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollForResult]);

  const handleFormChange = () => {
    setFormData(undefined);
  };

  return (
    <ResetSitecoreStyles>
      <span
        style={{ display: 'none' }}
        data-build-id={import.meta.env.NX_PUBLIC_BUILD_NUMBER}
        data-git-hash={import.meta.env.NX_PUBLIC_GIT_HASH}
      />
      <SapphireProvider theme={defaultTheme} locale={locale} width={'100%'} overlayZIndex={1000}>
        {displayLanguageSwitcher && <DevLanguageSwitcher languages={['fi', 'en']} />}
        <View position={'relative'}>
          <Flex justifyContent={'center'} width={'100%'}>
            <View
              marginBottom={{ base: 16, sm: 40, md: 72 }}
              width={{ base: '100%', sm: 696, md: 996 }}
              marginLeft={{ base: 8, sm: 12 }}
              marginRight={{ base: 8, sm: 12 }}
            >
              <BaseLayout border={'none'} columnGap={24} maxWidth={974}>
                <Flex justifyContent={'center'} width={'100%'}>
                  <View minWidth={{ base: '100%', sm: 336, md: 486 }}>
                    <FormLayout height={'fit-content'} borderRadius={tokens.size.radiusMd.value}>
                      <Form onSubmit={onSubmit} buttonRef={buttonRef} resetForm={handleFormChange} data={formData} />
                    </FormLayout>
                  </View>
                </Flex>
                <Flex justifyContent={'center'} width={'100%'} ref={layoutRef}>
                  <View minWidth={{ base: '100%', sm: 336, md: 486 }} position={'relative'} aria-disabled={!formData}>
                    {!formData && <TipsContainer />}
                    <ResultLayout
                      backgroundColor={tokens.color.backgroundSurface.value}
                      withSeparator={false}
                      withBorder={false}
                      isHidden={!formData}
                    >
                      <Results data={formData} />
                    </ResultLayout>
                  </View>
                </Flex>
              </BaseLayout>
            </View>
          </Flex>
        </View>
      </SapphireProvider>
    </ResetSitecoreStyles>
  );
}
